import React, { useEffect } from 'react'
import styled from 'styled-components'
import HeaderScreen from 'src/components/shared/header-screen/HeaderScreen'
import SubscriptionCard from './SubscriptionCard'
import { useDispatch, useSelector } from 'react-redux'
import { getPricesList } from 'src/features/SubscriptionSlice'
import Loading from 'src/components/styled/Loading'
import { getOllieFeatureGroup } from 'src/constants/olliePlanFeatures'
import {
  getTeamFeatureGroup,
  MOST_POPULAR_PLAN,
} from 'src/constants/planFeatureCategories'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import { foodSafetyPlanFeatureGroup } from 'src/constants/foodSafetyPlanFeatures'
import { recipePlanFeatureLines } from 'src/constants/recipePlanAllowances'

const OMSPlans = () => {
  const dispatch = useDispatch()
  const { isLoading, pricesList } = useSelector(state => state.subscriptions)
  const venue = useSelector(state => state.venue.venue)
  const subscriptions = venue?.newPricing ?? {}

  const isOnFreePlan = !['pro', 'growth', 'starter'].some(
    key => subscriptions[key]?.isSubscribed,
  )

  useEffect(() => {
    dispatch(getPricesList())
  }, [])

  const filteredPrices = pricesList
    .filter(price => ['Starter', 'Growth', 'Pro'].includes(price.nickname))
    .sort((a, b) => a.unit_amount - b.unit_amount)

  const withPlanFeatures = (plan, features) => [
    ...features,
    getTeamFeatureGroup(plan),
    getOllieFeatureGroup(plan),
  ]

  const featureList = {
    Free: {
      title:
        'Start ordering from your suppliers for free and discover how OMS can help you save time, control costs and run a more profitable kitchen.',
      features: [
        {
          title: 'Ordering & Operations',
          features: [
            'Order from unlimited suppliers',
            'Access past orders anytime',
            'Set up 1 standing order',
          ],
        },
        {
          title: 'Invoice & Reporting',
          features: [
            { text: 'Upload up to 5 invoices/month' },
            { text: 'View up to 3 price changes/month' },
          ],
        },
        {
          title: 'Menu Builder & Recipes',
          features: recipePlanFeatureLines('Free'),
        },
        {
          title: 'Goods Receipting',
          features: [
            { text: 'Report 1 PO/month in Goods Receipting' },
            {
              text: 'No automatic invoice reconciliation on Lite',
              limited: true,
            },
          ],
        },
        foodSafetyPlanFeatureGroup('Free'),
      ],
    },
    Starter: {
      title: 'Ideal for single-site operators',
      features: [
        {
          title: 'Ordering & Operations',
          features: [
            'Order from unlimited suppliers',
            'Access past orders anytime',
            'Set up 5 standing orders',
          ],
        },
        {
          title: 'Invoice & Reporting',
          features: [
            { text: 'View all price changes' },
            { text: 'Upload up to 25 invoices/month' },
          ],
        },
        {
          title: 'Menu Builder & Recipes',
          features: recipePlanFeatureLines('Starter'),
        },
        {
          title: 'Goods Receipting',
          features: [
            { text: 'Report up to 5 purchase orders/month' },
            'Automatic invoice reconciliation',
            'Access to GP% snapshot reports',
          ],
        },
        foodSafetyPlanFeatureGroup('Starter'),
      ],
    },
    Pro: {
      title: 'Ideal for serious multi-site operators',
      features: [
        {
          title: 'Ordering & Operations',
          features: ['Everything in Growth', 'Unlimited standing orders'],
        },
        {
          title: 'Invoice & Reporting',
          features: [
            { text: 'Full price tracking' },
            { text: 'Advanced reporting tools' },
            { text: 'Accounting software integration (e.g. Xero)' },
            { text: 'Multi-site spend visibility (if applicable)' },
          ],
        },
        {
          title: 'Menu Builder & Recipes',
          features: recipePlanFeatureLines('Pro'),
        },
        {
          title: 'Goods Receipting',
          features: [
            { text: 'Save unlimited purchase orders' },
            'Automatic invoice reconciliation',
            'Access to Stock take feature',
            'Dedicated account manager',
            'EPOS integration',
          ],
        },
        foodSafetyPlanFeatureGroup('Pro'),
      ],
    },
    Growth: {
      title: 'Ideal for growing or multi-site operators',
      features: [
        {
          title: 'Ordering & Operations',
          features: [
            'Everything in Starter',
            'Set up up to 20 standing orders',
          ],
        },
        {
          title: 'Invoice & Reporting',
          features: [
            { text: 'Accounting software integration (e.g. Xero)' },
            { text: 'Full price change visibility' },
            { text: 'Upload up to 100 invoices/month' },
          ],
        },
        {
          title: 'Menu Builder & Recipes',
          features: recipePlanFeatureLines('Growth'),
        },
        {
          title: 'Goods Receipting',
          features: [
            { text: 'Report up to 20 purchase orders/month' },
            'Automatic invoice reconciliation',
          ],
        },
        foodSafetyPlanFeatureGroup('Growth'),
      ],
    },
  }

  return (
    <Wrapper>
      <HeaderRow>
        <HeaderLeft>
          <HeaderScreen
            title='Pricing Plans'
            subTitle='Choose a plan to suit your business.'
          />
        </HeaderLeft>
        <SalesHelp>
          <SalesTitle>Not sure which plan fits?</SalesTitle>
          <SalesText>
            Interested in annual billing? Bespoke annual plans and discounts are
            arranged case by case.
          </SalesText>
          <GrowthEngineHumanDemoRow />
        </SalesHelp>
      </HeaderRow>
      {isLoading ? (
        <Loading />
      ) : (
        <Section>
          <SubscriptionCard
            title={featureList['Free'].title}
            plan='Free'
            price={0}
            features={withPlanFeatures('Free', featureList['Free'].features)}
            priceId={null}
            isSubscribed={isOnFreePlan}
          />
          {filteredPrices?.map(price => (
            <SubscriptionCard
              isSubscribed={
                subscriptions[price.nickname?.toLowerCase()]?.isSubscribed ??
                false
              }
              isPopular={price.nickname === MOST_POPULAR_PLAN}
              key={price.nickname}
              title={featureList[price.nickname].title}
              plan={price.nickname}
              price={price.unit_amount}
              features={withPlanFeatures(
                price.nickname,
                featureList[price.nickname].features,
              )}
              priceId={price.id}
            />
          ))}
        </Section>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding: 20px;
  background-color: ${({ theme }) => theme.colours.bgGrey};
  min-height: 100vh;
`

const Section = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 20px;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
`

const SalesHelp = styled.div`
  flex-shrink: 0;
  width: min(100%, 400px);
  padding: 16px 18px;
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 12px;
  text-align: left;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    width: 100%;
    max-width: 480px;
  }
`

const SalesTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const SalesText = styled.p`
  margin: 0 0 6px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.bbqBlack};

  &:last-child {
    margin-bottom: 0;
  }
`

export default OMSPlans
